import { AbsenceStatus, type EnhancedAbsenceModel } from '@app/reducers/orm/absence/absence.model';
import { type Dictionary } from '@ngrx/entity';
import { type DepartmentModel } from '@reducers/orm/department/department.model';
import { type LocationModel } from '@reducers/orm/location/location.model';
import { type TeamModel } from '@reducers/orm/team/team.model';

/**
 * The sections the step renders, in display order. An absence carries its review state on
 * `status`, so the category *is* the status — there is nothing to compute.
 */
export type PayrollAbsenceCategory = 'PENDING' | 'APPROVED' | 'DECLINED';

// Partial, not a total `Record`: a status string outside the enum can genuinely arrive from the
// API, and the lookup below has to be able to miss rather than crash on it.
export const PAYROLL_ABSENCE_CATEGORY_BY_STATUS: Partial<Record<AbsenceStatus, PayrollAbsenceCategory>> = {
  [AbsenceStatus.PENDING]: 'PENDING',
  [AbsenceStatus.APPROVED]: 'APPROVED',
  [AbsenceStatus.DECLINED]: 'DECLINED',
};

/** An absence plus the org-chart names the row shows beneath the employee. */
export interface PayrollAbsenceRow {
  absence: EnhancedAbsenceModel;
  teamName: string | null;
  departmentName: string | null;
  locationName: string | null;
  // `null`, not `PayrollAbsenceCategory`, matching the lookup that fills it: an unrecognised
  // status has no section to go in.
  category: PayrollAbsenceCategory | null;
}

export type PayrollAbsenceGroups = Record<PayrollAbsenceCategory, PayrollAbsenceRow[]>;

/** A fresh object per call — the sections must never share array instances. */
export function emptyPayrollAbsenceGroups(): PayrollAbsenceGroups {
  return { PENDING: [], APPROVED: [], DECLINED: [] };
}

export function buildPayrollAbsenceRow(
  absence: EnhancedAbsenceModel,
  teamsById: Dictionary<TeamModel>,
  departmentsById: Dictionary<DepartmentModel>,
  locationsById: Dictionary<LocationModel>,
): PayrollAbsenceRow {
  const team = resolveTeam(absence, teamsById);
  const department = team ? departmentsById[team.department_id] : undefined;
  const location = department ? locationsById[department.location_id] : undefined;

  return {
    absence,
    teamName: team?.name ?? null,
    departmentName: department?.name ?? null,
    locationName: location?.name ?? null,
    category: PAYROLL_ABSENCE_CATEGORY_BY_STATUS[absence.status] ?? null,
  };
}

/**
 * An employee can belong to several teams; the row shows the first still-resolvable one. A
 * deleted team, or one this manager cannot see, falls through to the next candidate rather than
 * blanking the whole org-chart line.
 */
function resolveTeam(absence: EnhancedAbsenceModel, teamsById: Dictionary<TeamModel>): TeamModel | undefined {
  for (const teamId of absence.Employee?.Team ?? []) {
    const team = teamsById[teamId];

    if (team) {
      return team;
    }
  }

  return undefined;
}
